import { isCoachReviewArtifactViewed, type CoachReviewResolvedSource } from "@/lib/coachReviewArtifacts";
import { resolveCoachEvidenceReferenceResult, type CoachEvidenceCanonicalIdentity } from "@/lib/coachEvidenceResolver";
import type { MeaningfulNoticingCard } from "@/components/coach/sessionPrep/MeaningfulNoticingSection";
import type { EvidenceTimelineItem } from "@/components/coach/sessionPrep/EvidenceTimelineSection";

type EvidenceTimelineEntry = EvidenceTimelineItem & {
  subtype: string | null;
  meeting_id: string | null;
  canonicalIdentity: CoachEvidenceCanonicalIdentity;
};

function toCoachReviewResolvedSource(item: EvidenceTimelineEntry | null): CoachReviewResolvedSource | null {
  if (!item) return null;
  if (item.source === "Transcript") {
    return { source: "Transcript", itemId: item.canonicalIdentity.sourceId, meetingId: item.meeting_id };
  }
  if (item.subtype === "shared-reflection") {
    return { source: "Shared Reflection", itemId: item.canonicalIdentity.sourceId, meetingId: item.meeting_id };
  }
  if (item.subtype === "reflection" || item.source === "Reflection") {
    return { source: "Reflection", itemId: item.canonicalIdentity.sourceId, meetingId: item.meeting_id };
  }
  if (item.source === "Artifact") {
    return { source: "Artifact", itemId: item.canonicalIdentity.sourceId, meetingId: item.meeting_id };
  }
  return { source: "Evidence", itemId: item.canonicalIdentity.sourceId, meetingId: item.meeting_id };
}

export function toCanonicalCoachEvidenceReference(item: EvidenceTimelineEntry) {
  return {
    label: item.title,
    link: item.link,
    evidenceId: item.id,
  };
}

export function applyCoachNoticingViewState(
  cards: MeaningfulNoticingCard[],
  evidenceItems: EvidenceTimelineEntry[],
  viewedArtifactKeys: Set<string>,
): MeaningfulNoticingCard[] {
  return cards.map((card) => {
    const evidence = card.evidence.map((entry) => {
      const resolution = resolveCoachEvidenceReferenceResult(evidenceItems, entry);
      const resolvedMatch = resolution.status === "resolved" ? resolution.item : null;
      const resolvedSource = toCoachReviewResolvedSource(resolvedMatch);
      const canonicalReference = resolvedMatch
        ? toCanonicalCoachEvidenceReference(resolvedMatch)
        : null;
      return {
        ...entry,
        viewed: resolvedSource && canonicalReference
          ? isCoachReviewArtifactViewed(viewedArtifactKeys, {
              ...canonicalReference,
              resolvedSource,
            })
          : false,
      };
    });

    return {
      ...card,
      evidence,
      reviewed: Boolean(card.reviewed || evidence.some((entry) => entry.viewed)),
    };
  });
}
